import {
    setEngine, sourceLoading, sourceLoaded, tracksDescribed, clearSource, buildStarted, buildProgress, buildStopping,
    buildFinished, buildStopped, buildFailed, openSet, setSets, removeTrack,
    setOrderMode, revealStarted, revealProgressed, revealStopping, revealApplied, revealEnded, revealFailed, resetMyOrder,
    orderSignature, isRevealShown, setCoverPick,
} from '../slices/automixSlice';
import { pushNavEntry } from '../slices/appSlice';
import { refreshAndRetry } from '../utils/refreshAndRetry';
import { shapeServerTracks } from '../../utils/cloudPlaylistTracks';
import { track as trackEvent } from '../../utils/analytics';
import { showUpgradeModal } from '../slices/tierSlice';
import { automixIncluded } from '../../utils/automixFormat';
import { DEFAULT_TRACK_COVER, DEFAULT_PLAYLIST_COVER } from '../../utils/cover';
import { primeArtThumbs } from '../../utils/artThumbStore';

const isLocalFile = (p) => !!p && !/^(online:|https?:)/i.test(p);
// A row's cover only when it is a real URL. resolveCover() fills a missing one
// with the default tile, and imported rows can carry their art as a base64
// blob (hundreds of KB each — not something to hold 40 of in Redux); both
// fall back to the file's own embedded art via the album-art route.
const realCover = (c) => (typeof c === 'string' && /^https?:\/\//i.test(c) && c !== DEFAULT_TRACK_COVER ? c : null);
// A cloud row whose art is the app's own `/album-art?id=N` names a library
// track — the id its thumbnail is filed under, even when the file isn't here.
const idFromCover = (c) => { const m = /\/album-art\?id=(\d+)/.exec(c || ''); return m ? Number(m[1]) : null; };

// A row completed by what the library, or the file itself, said about it
// (automix:describeTracks). The row's own title, artist and remote art win.
const describedFields = (r, d) => {
    const id = d.id ?? r.id ?? null;
    return {
        id,
        title: r.title === 'Unknown' && d.title ? d.title : r.title,
        artist: r.artist || d.artist || '',
        bpm: d.bpm ?? r.bpm ?? null,
        musicalKey: d.key || r.musicalKey || '',
        duration: d.duration ?? r.duration ?? null,
        loudness: d.loudness ?? r.loudness ?? null,
        // remote art first; a library track's own art by id (disk-cached, and
        // the rows take its thumbnail); a file the library doesn't know stays
        // on its path
        cover: r.cover || d.cover || (id != null ? `http://localhost:3069/album-art?id=${id}` : null),
    };
};
const withDescriptions = (rows, described) => {
    const byPath = new Map((described || []).map((d) => [d.path, d]));
    return rows.map((r) => {
        const d = r.path ? byPath.get(r.path) : null;
        return d ? { ...r, ...describedFields(r, d) } : r;
    });
};

// The rows as the engine takes them.
const engineTracks = (rows) => rows.map((r) => ({
    path: r.path, title: r.title, artist: r.artist, bpm: r.bpm, key: r.musicalKey, duration: r.duration, loudness: r.loudness,
}));

// The engine runs one job at a time: a "Show order" still running is stopped
// (and waited for) before anything else asks for the engine.
let orderRun = null;
async function settleOrder(dispatch, getState) {
    if (getState().automix.revealPhase === 'idle' || !orderRun) return;
    dispatch(revealStopping());
    try { await window.electron.automix.cancel(); } catch (e) { /* settles either way */ }
    try { await orderRun; } catch (e) { /* ditto */ }
}

/** What this machine can do. Asked once at boot: the Prepare tile depends on it. */
export const loadAutomixEngine = () => async (dispatch, getState) => {
    try {
        const status = await window.electron?.automix?.getStatus?.(getState().automixSettings.outDir || undefined);
        dispatch(setEngine(status || { available: false, reason: 'bridge-missing' }));
    } catch (e) {
        dispatch(setEngine({ available: false, reason: 'bridge-missing' }));
    }
};

/**
 * Load a playlist's rows for the setup screen. Every row is kept — the ones
 * with no audio file on this machine are shown as such and left out of the
 * set — and the local ones get the library's tempo/key (what the engine
 * plans with).
 * @param {{kind: 'cloud'|'local', id: string|number, name: string, cover?: string}} source
 */
export const selectAutomixSource = (source, from = 'picker') => async (dispatch, getState) => {
    await settleOrder(dispatch, getState);
    dispatch(sourceLoading(source));
    dispatch(setOrderMode(getState().automixSettings.order));
    const stillCurrent = () => {
        const cur = getState().automix.source;
        return cur && cur.kind === source.kind && String(cur.id) === String(source.id);
    };
    const startedAt = Date.now();
    let shown = false;
    try {
        let rows = [];
        if (source.kind === 'cloud') {
            const result = await refreshAndRetry((token) => window.electron.library.getPlaylist(token, source.id));
            const data = result?.data || result;
            if (!data || data.error) throw new Error(data?.error || 'playlist not found');
            const shaped = await shapeServerTracks(data, { accessToken: getState().auth.accessToken });
            rows = shaped.map((t, i) => ({
                key: `c${t.id ?? i}`,
                id: idFromCover(t.cover),
                path: t.filePath && !t.missingLocal && isLocalFile(t.filePath) ? t.filePath : null,
                title: t.name || t.title || 'Unknown',
                artist: t.singer || t.artist || '',
                cover: realCover(t.cover),
                isMix: !!t.mixPublicId,
            }));
        } else {
            const data = await window.electron.db.playlists.get(source.id, true);
            rows = (data?.tracks || []).map((t, i) => {
                const p = t.file || t.filePath || t.file_path || null;
                return {
                    key: `l${t.id ?? i}`,
                    id: t.track?.id ?? null,
                    path: isLocalFile(p) ? p : null,
                    title: t.track?.title || t.title || t.fileName || 'Unknown',
                    artist: t.track?.artist || t.artist || '',
                    cover: realCover(t.track?.cover || t.cover),
                    isMix: false,
                };
            });
        }
        rows = rows.map((r, i) => ({ ...r, pos: i }));   // playlist position (Best order shows the list this way)
        const paths = rows.map((r) => r.path).filter(Boolean);
        // Two steps, so the rows never wait for the files. First what the
        // library already knows — instant: id, art, tempo, key — and the rows
        // go up with their covers. Then the files themselves (is it there,
        // whatever the library lacked), a few at a time: a 50-track playlist
        // on an external drive takes seconds to read, which used to be
        // seconds of skeleton.
        const describe = window.electron.automix.describeTracks;
        const known = paths.length ? await describe(paths, { files: false }) : [];
        rows = withDescriptions(rows, known);
        // …and their row thumbnails into RAM (utils/artThumbStore) before the
        // rows mount, so the covers paint in the same tick as the titles. One
        // batch request, ~20 ms from the disk cache; capped so a slow answer
        // can't hold the rows — the setup screen asks again for what's missing.
        const ids = rows.map((r) => r.id).filter((id) => id != null);
        if (ids.length) await Promise.race([primeArtThumbs(ids), new Promise((r) => setTimeout(r, 400))]);
        if (!stillCurrent()) return;
        let cover = null;
        if (!source.cover || source.cover === DEFAULT_PLAYLIST_COVER) {
            const first = rows.find((r) => r.cover || r.path);
            if (first) cover = first.cover || `http://localhost:3069/album-art?path=${encodeURIComponent(first.path)}`;
        }
        dispatch(sourceLoaded({ tracks: rows, cover, describing: paths.length > 0 }));
        shown = true;
        const shownAt = Date.now();
        const described = paths.length ? await describe(paths) : [];
        if (!stillCurrent()) return;
        const byPath = new Map((described || []).map((d) => [d.path, d]));
        const byKey = {};
        rows.forEach((r) => {
            const d = r.path ? byPath.get(r.path) : null;
            if (!d) return;
            byKey[r.key] = d.exists === false ? { path: null } : describedFields(r, d);
        });
        dispatch(tracksDescribed({ byKey }));
        const cur = getState().automix.tracks;
        const local = cur.filter((r) => r.path).length;
        trackEvent('playlist_opened_in_tool', {
            tab: 'automix', source: source.kind, track_count: local, skipped_count: cur.length - local, from,
            shown_ms: shownAt - startedAt, described_ms: Date.now() - shownAt,
        });
    } catch (error) {
        console.error('[Automix] failed to load playlist:', error);
        // rows already on screen stay (as the playlist described them)
        if (stillCurrent()) dispatch(shown ? tracksDescribed({ byKey: {} }) : sourceLoaded({ tracks: [] }));
        throw error;
    }
};

/** Leave the playlist (the × on the source bar). */
export const closeAutomixSource = () => async (dispatch, getState) => {
    await settleOrder(dispatch, getState);
    dispatch(clearSource());
};

/**
 * "Reveal the order": MixGoat plans the running order now — analysis and
 * planning only, no render, the pinned opener/closer honoured — and Best
 * order shows it in the list. A build then plays exactly that order and skips
 * the planning already done. From My order it switches to Best order; the
 * user's order stays under My order. A result for inputs that changed
 * meanwhile is dropped.
 */
export const showAutomixOrder = () => async (dispatch, getState) => {
    const { automix } = getState();
    if (automix.revealPhase !== 'idle' || automix.phase === 'building' || automix.phase === 'stopping') return null;
    const usable = automixIncluded(automix.tracks).included;
    if (usable.length < 2) return null;
    const flow = automix.orderMode !== 'playlist';
    const opener = automix.openerKey ? usable.findIndex((r) => r.key === automix.openerKey) : -1;
    const closer = automix.closerKey ? usable.findIndex((r) => r.key === automix.closerKey) : -1;
    const sourceId = automix.source ? `${automix.source.kind}:${automix.source.id}` : null;
    const sig = orderSignature(automix);
    const props = { track_count: usable.length, from: flow ? 'best_flow' : 'my_order', has_opener: opener >= 0, has_closer: closer >= 0 };
    const startedAt = Date.now();
    dispatch(revealStarted());
    let result;
    orderRun = window.electron.automix.order({
        tracks: engineTracks(usable),
        firstIndex: opener >= 0 ? opener : null,
        lastIndex: closer >= 0 ? closer : null,
    }, (progress) => dispatch(revealProgressed(progress)));
    try { result = await orderRun; } catch (error) {
        result = { error: { code: 'BUILD_FAILED', message: error?.message || 'The order could not be found' } };
    } finally { orderRun = null; }
    const seconds = Math.round((Date.now() - startedAt) / 1000);
    // another playlist (or account) took over, or the tracks/pins changed
    // meanwhile: the result answers a question nobody is asking any more
    const cur = getState().automix;
    if (!cur.source || `${cur.source.kind}:${cur.source.id}` !== sourceId || orderSignature(cur) !== sig) {
        dispatch(revealEnded());
        return null;
    }
    if (result?.ok) {
        // path -> row key; a playlist can hold the same file twice
        const byPath = new Map();
        usable.forEach((r) => { if (!byPath.has(r.path)) byPath.set(r.path, []); byPath.get(r.path).push(r.key); });
        const keys = (result.order || []).map((o) => byPath.get(o.path)?.shift()).filter(Boolean);
        dispatch(revealApplied({ keys, sig }));
        trackEvent('automix_order_shown', { ...props, seconds });
    } else if (result?.cancelled) {
        dispatch(revealEnded());
        trackEvent('automix_order_show_stopped', { ...props, seconds });
    } else if (result?.error?.code === 'UPGRADE_REQUIRED') {
        dispatch(revealEnded());
        dispatch(showUpgradeModal({ feature: 'automix', context: { from: 'automix_show_order' } }));
    } else {
        dispatch(revealFailed(result?.error || { code: 'BUILD_FAILED', message: 'The order could not be found' }));
    }
    return result;
};

export const stopAutomixOrder = () => async (dispatch, getState) => {
    if (getState().automix.revealPhase !== 'running') return;
    dispatch(revealStopping());
    try { await window.electron.automix.cancel(); } catch (e) { /* the order promise settles either way */ }
};

export const resetAutomixOrder = () => (dispatch, getState) => {
    const { myOrder, tracks } = getState().automix;
    if (!myOrder) return;
    dispatch(resetMyOrder());
    trackEvent('automix_order_reset', { track_count: automixIncluded(tracks).included.length });
};

export const loadAutomixSets = () => async (dispatch, getState) => {
    try {
        const sets = await window.electron.automix.listSets(getState().automixSettings.outDir || undefined);
        dispatch(setSets(sets || []));
    } catch (e) {
        dispatch(setSets([]));
    }
};

// Opening a set is a navigation: the set view is a page inside the tab, so it
// needs its own nav entry or Back pops the whole tab (landing on the Prepare
// grid instead of the shelf you came from). `push: false` is the restore path
// — navigateHistory reopening the set must not push the entry again.
export const openAutomixSet = (dir, from = 'shelf', { push = true } = {}) => async (dispatch, getState) => {
    const set = await window.electron.automix.readSet(dir);
    if (!set) return null;
    dispatch(openSet(set));
    if (push) {
        const { app } = getState();
        dispatch(pushNavEntry({ category: app.activeCategory, tab: 'automix', automix: { setDir: dir } }));
    }
    trackEvent('automix_set_opened', { track_count: set.trackCount, duration_sec: Math.round(set.durationSec || 0), from });
    return set;
};

/**
 * The set's picture. A set is built wearing the playlist's artwork; from here
 * the user can put their own on it, or take it off and go back to the mosaic
 * of its first four tracks. Both write into the set's own folder, so the
 * picture travels with the set.
 */
export const chooseSetCover = () => async (dispatch, getState) => {
    const dir = getState().automix.set?.dir;
    if (!dir) return false;
    const res = await window.electron.automix.pickCover(dir);
    if (res?.canceled) return false;
    if (!res?.ok || !res.set) return false;
    dispatch(openSet(res.set));
    dispatch(loadAutomixSets());
    trackEvent('automix_set_cover_changed', { source: 'file', from: 'set' });
    return true;
};

/**
 * The setup screen's cover. The set has no folder yet, so the choice is held
 * in Redux and written when the build finishes; until then the rail shows the
 * thumbnail the main process handed back.
 */
export const pickSetupCover = () => async (dispatch) => {
    const res = await window.electron.automix.pickImage();
    if (res?.canceled || !res?.path || !res?.preview) return false;
    dispatch(setCoverPick({ path: res.path, preview: res.preview }));
    trackEvent('automix_set_cover_changed', { source: 'file', from: 'setup' });
    return true;
};

/** Back to the playlist's picture (or the default artwork if it has none). */
export const clearSetupCover = () => (dispatch) => {
    dispatch(setCoverPick(null));
    trackEvent('automix_set_cover_changed', { source: 'removed', from: 'setup' });
};

export const clearSetCover = () => async (dispatch, getState) => {
    const dir = getState().automix.set?.dir;
    if (!dir) return false;
    const res = await window.electron.automix.clearCover(dir);
    if (!res?.set) return false;
    dispatch(openSet(res.set));
    dispatch(loadAutomixSets());
    trackEvent('automix_set_cover_changed', { source: 'removed', from: 'set' });
    return true;
};

/** Build the set from the loaded playlist. Resolves with the engine's result. */
export const buildAutomixSet = () => async (dispatch, getState) => {
    if (['building', 'stopping'].includes(getState().automix.phase)) return null;
    await settleOrder(dispatch, getState);
    const { automix, automixSettings } = getState();
    const usable = automixIncluded(automix.tracks).included;
    const flow = automix.orderMode !== 'playlist';
    // Best order with MixGoat's order on screen: build exactly that order
    // (identical to what the engine would pick; it skips re-planning it)
    const shown = isRevealShown(automix);
    const opener = flow && !shown && automix.openerKey ? usable.findIndex((r) => r.key === automix.openerKey) : -1;
    const closer = flow && !shown && automix.closerKey ? usable.findIndex((r) => r.key === automix.closerKey) : -1;
    const stems = !!(automixSettings.stems && automix.engine?.stemsAvailable);
    const props = {
        track_count: usable.length,
        skipped_count: automix.tracks.length - usable.length,
        order: flow ? 'flow' : 'playlist',
        revealed: shown,
        order_origin: flow ? null : automix.orderOrigin,
        has_opener: flow && !!automix.openerKey,
        has_closer: flow && !!automix.closerKey,
        stems,
        source: automix.source?.kind || 'unknown',
    };
    const startedAt = Date.now();
    dispatch(buildStarted(startedAt));
    trackEvent('automix_build_started', props);
    let result;
    try {
        result = await window.electron.automix.build({
            name: (automix.setName || automix.source?.name || 'Automix set').trim(),
            playlist: automix.source?.name || null,
            // the picture chosen on the setup screen, else the playlist's
            // (sourceLoaded falls back to the first track's art when the
            // playlist has none)
            cover: automix.coverPick?.path || automix.source?.cover || null,
            outDir: automixSettings.outDir || undefined,
            keepOrder: !flow || shown,
            firstIndex: opener >= 0 ? opener : null,
            lastIndex: closer >= 0 ? closer : null,
            stems,
            tracks: engineTracks(usable),
        }, (progress) => dispatch(buildProgress(progress)));
    } catch (error) {
        result = { error: { code: 'BUILD_FAILED', message: error?.message || 'The build could not be started' } };
    }
    const duration_sec = Math.round((Date.now() - startedAt) / 1000);
    if (result?.ok && result.set) {
        dispatch(buildFinished(result.set));
        dispatch(loadAutomixSets());
        trackEvent('automix_build_completed', {
            ...props, build_sec: duration_sec, set_sec: Math.round(result.set.durationSec || 0), warning_count: (result.warnings || []).length,
        });
    } else if (result?.error?.code === 'UPGRADE_REQUIRED') {
        // the main process is the check that cannot be skipped: back to the
        // setup, paywall up, nothing "failed"
        dispatch(buildStopped());
        dispatch(showUpgradeModal({ feature: 'automix', context: { from: 'automix_build' } }));
    } else if (result?.cancelled) {
        const stage = getState().automix.progress?.stage || null; // buildStopped clears it
        dispatch(buildStopped());
        trackEvent('automix_build_stopped', { ...props, build_sec: duration_sec, stage });
    } else {
        const error = result?.error || { code: 'BUILD_FAILED', message: 'The build failed' };
        dispatch(buildFailed(error));
        trackEvent('automix_build_failed', { ...props, build_sec: duration_sec, code: error.code });
    }
    return result;
};

/** After the sync gate refused a build: drop the tracks it named and go again. */
export const rebuildWithoutOffenders = () => async (dispatch, getState) => {
    const { error, tracks } = getState().automix;
    const paths = new Set((error?.offenders || []).map((o) => o.path));
    const rows = tracks.filter((t) => t.path && paths.has(t.path));
    if (!rows.length) return null;
    rows.forEach((t) => dispatch(removeTrack(t.key)));
    trackEvent('tracks_removed_from_list', { tab: 'automix', count: rows.length, via: 'sync_gate' });
    return dispatch(buildAutomixSet());
};

export const stopAutomixBuild = () => async (dispatch, getState) => {
    const { phase } = getState().automix;
    if (phase !== 'building') return;
    dispatch(buildStopping());
    try { await window.electron.automix.cancel(); } catch (e) { /* the build promise settles either way */ }
};
